import React, { useState, useRef } from "react";
import { referenceGenerate, referenceStatus, imageSendApproval, imageToSocial } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  UploadSimple, X, Sparkle, ShieldCheck, ImageSquare, FacebookLogo, InstagramLogo,
  PaperPlaneTilt, CheckCircle, DownloadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ASPECTS = [{ v: "square", l: "Square (Instagram)" }, { v: "portrait", l: "Portrait (Story/Reel)" }, { v: "landscape", l: "Landscape (Website/Cover)" }];

export function ReferenceStudio({ clientId }) {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]); // {file, url}
  const [prompt, setPrompt] = useState("");
  const [variations, setVariations] = useState(3);
  const [aspect, setAspect] = useState("square");
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [sent, setSent] = useState(() => new Set());

  const addFiles = (list) => {
    const imgs = Array.from(list || []).filter((f) => f.type.startsWith("image/")).slice(0, 4);
    if (!imgs.length) return;
    setFiles((prev) => [...prev, ...imgs.map((f) => ({ file: f, url: URL.createObjectURL(f) }))].slice(0, 4));
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const onDrop = (e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); };

  const genOne = async (conceptIndex, attempt = 0) => {
    const fd = new FormData();
    fd.append("client_id", clientId);
    fd.append("prompt", prompt);
    fd.append("variations", "1");
    fd.append("concept_index", String(conceptIndex));
    fd.append("aspect", aspect);
    files.forEach(({ file }) => fd.append("files", file));
    try {
      // 1) start async job (returns instantly — no long-held connection = no 524)
      const start = await referenceGenerate(fd);
      const jobId = start.job_id;
      if (!jobId) throw new Error("no job");
      // 2) poll until done/error (max ~150s), every 3s
      const deadline = Date.now() + 150000;
      let img = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await referenceStatus(jobId).catch(() => null);
        if (!s) continue;
        if (s.status === "done" && s.image_path) { img = { image_path: s.image_path, concept: s.concept }; break; }
        if (s.status === "error") throw new Error(s.error || "generation error");
      }
      if (!img) throw new Error("timeout");
      setResults((prev) => [...prev, img]);
      return true;
    } catch (e) {
      if (attempt < 1) return genOne(conceptIndex, attempt + 1); // one sequential retry
      return false;
    } finally {
      setProgress((p) => ({ ...p, done: Math.min(p.done + 1, p.total) }));
    }
  };

  const generate = async () => {
    if (!files.length) { toast.error("Upload at least one product photo"); return; }
    setBusy(true); setResults([]);
    const total = variations;
    setProgress({ done: 0, total });
    const indices = Array.from({ length: total }, (_, i) => i);
    let ok = 0, failed = 0;
    // concurrency pool of 2 (stays under Cloudflare timeout & API rate limits)
    const CONCURRENCY = 2;
    let cursor = 0;
    const worker = async () => {
      while (cursor < indices.length) {
        const idx = indices[cursor++];
        const success = await genOne(idx);
        if (success) ok++; else failed++;
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, total) }, worker));
      if (ok && failed) toast.warning(`Generated ${ok} of ${total} — ${failed} failed, you can regenerate`);
      else if (ok) toast.success(`Generated ${ok} branded variation(s)`);
      else toast.error("Generation failed. Try again.");
    } finally { setBusy(false); }
  };

  const toQueue = async (img) => {
    try { await imageSendApproval({ client_id: clientId, image_path: img.image_path, title: `Reference · ${img.concept}` });
      setSent((s) => new Set(s).add(`${img.image_path}-q`)); toast.success("Sent to Approval Queue"); }
    catch { toast.error("Could not send to queue"); }
  };
  const toSocial = async (img, platform) => {
    try { await imageToSocial(clientId, img.image_path, platform);
      setSent((s) => new Set(s).add(`${img.image_path}-${platform}`)); toast.success(`Sent to Social Manager (${platform}) — caption added, review in queue`); }
    catch { toast.error("Could not send to Social"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5" data-testid="reference-studio">
      <div className="lg:col-span-2 space-y-4">
        <div className="card-matte p-5 space-y-4">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-emerald-400" weight="fill" />
            <span className="text-sm text-[#cfcfcf]">Your real product is <b className="text-white">composited into AI scenes</b> — logos, packaging &amp; branding stay pixel-exact.</span>
          </div>

          <div
            data-testid="reference-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${dragging ? "border-[#6B3DFF] bg-[#6B3DFF]/10" : "border-white/15 hover:border-white/30"}`}>
            <UploadSimple size={26} className="mx-auto mb-2 text-[#9a9a9a]" />
            <div className="text-sm text-[#cfcfcf]">Drag &amp; drop product photos, or click to browse</div>
            <div className="text-[11px] text-[#7a7a7a] mt-1">Up to 4 images · PNG/JPG · transparent PNG works best</div>
            <input ref={inputRef} type="file" accept="image/*" multiple hidden data-testid="reference-file-input"
              onChange={(e) => addFiles(e.target.files)} />
          </div>

          {files.length > 0 && (
            <div className="grid grid-cols-4 gap-2" data-testid="reference-previews">
              {files.map((f, i) => (
                <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-white/10 bg-[#10101A]">
                  <img src={f.url} alt="ref" className="w-full h-full object-cover" />
                  <button data-testid={`remove-ref-${i}`} onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                    className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100"><X size={12} /></button>
                </div>
              ))}
            </div>
          )}

          <div><Label className="label-caps">Scene / marketing brief (optional)</Label>
            <Textarea data-testid="reference-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
              className="bg-[#10101A] border-white/10 mt-1" placeholder="e.g. cozy autumn café table with warm morning light" /></div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-caps">Variations</Label>
              <Select value={String(variations)} onValueChange={(v) => setVariations(Number(v))}>
                <SelectTrigger className="bg-[#10101A] border-white/10 mt-1" data-testid="reference-variations"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14141F] border-white/10 text-white">{[1, 2, 3, 4].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="label-caps">Aspect ratio</Label>
              <Select value={aspect} onValueChange={setAspect}>
                <SelectTrigger className="bg-[#10101A] border-white/10 mt-1" data-testid="reference-aspect"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14141F] border-white/10 text-white">{ASPECTS.map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <Button data-testid="reference-generate-btn" onClick={generate} disabled={busy || !files.length}
            className="w-full bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
            <Sparkle size={16} className="mr-1.5" weight="fill" /> {busy ? `Generating ${Math.min(progress.done + 1, progress.total)} of ${progress.total}…` : "Generate marketing variations"}
          </Button>
        </div>
      </div>

      <div className="lg:col-span-3">
        <div className="card-matte p-5 min-h-[300px]">
          {results.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16 text-[#7a7a7a]">
              <ImageSquare size={40} className="mb-3 opacity-40" />
              <div className="text-sm" data-testid="reference-empty-state">{busy ? `Generating ${progress.total} branded variation(s)… ${progress.done}/${progress.total} done` : "Your branded marketing variations will appear here."}</div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="reference-results">
              {busy && progress.done < progress.total && (
                <div className="col-span-full text-[11px] text-[#7a7a7a]" data-testid="reference-progress">Generated {results.length} of {progress.total} — still working on the rest…</div>
              )}
              {results.map((img, i) => (
                <div key={i} className="rounded-xl border border-white/10 overflow-hidden bg-[#10101A]" data-testid={`reference-result-${i}`}>
                  <img src={`${BACKEND}${img.image_path}`} alt={img.concept} className="w-full object-cover" />
                  <div className="p-3 space-y-2">
                    <div className="text-[11px] uppercase tracking-wider text-[#7a7a7a]">{img.concept}</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button data-testid={`ref-to-queue-${i}`} onClick={() => toQueue(img)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-[#cfcfcf]">
                        {sent.has(`${img.image_path}-q`) ? <CheckCircle size={12} weight="fill" className="text-emerald-400" /> : <PaperPlaneTilt size={12} />} Approval Queue
                      </button>
                      <button data-testid={`ref-to-fb-${i}`} onClick={() => toSocial(img, "Facebook")}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-sky-400">
                        {sent.has(`${img.image_path}-Facebook`) ? <CheckCircle size={12} weight="fill" /> : <FacebookLogo size={12} />} Facebook
                      </button>
                      <button data-testid={`ref-to-ig-${i}`} onClick={() => toSocial(img, "Instagram")}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-pink-400">
                        {sent.has(`${img.image_path}-Instagram`) ? <CheckCircle size={12} weight="fill" /> : <InstagramLogo size={12} />} Instagram
                      </button>
                      <a data-testid={`ref-download-${i}`} href={`${BACKEND}${img.image_path}`} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-[#7a7a7a] hover:text-white"><DownloadSimple size={12} /> Open</a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
